import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import SideBarContainer from 'Containers/SideBarContainer';
import { Padding } from 'Components/Padding';
import { IconToggleGroup, type IconToggleOption } from 'Components/IconToggleGroup';
import { CoursesReportIcon, EmployeesReportIcon } from 'Pages/reports/components/icons';
import ReportsLayout from 'Pages/reports/components/ReportsLayout/ReportsLayout';
import ReportsTitle from 'Pages/reports/components/ReportsTitle/ReportsTitle';
import SessionsReportFilters from './components/SessionsReportFilters';
import SessionsReportKpis from './components/SessionsReportKpis';
import SessionsByEmployeeTable from './components/SessionsByEmployeeTable';
import useSessionsReportFilters from './hooks/useSessionsReportFilters';
import useSessionEmployeesPage from './hooks/useSessionEmployeesPage';
import type { SessionsReportFilterValues } from './schemas/sessions-report-schemas';

type SessionsViewMode = 'employees' | 'courses';

export default function SessionEmployeesPage() {
  const history = useHistory();
  const location = useLocation();

  const form = useSessionsReportFilters();
  const { handleSubmit } = form;

  const {
    report,
    pagination,
    loading,
    error,
    exporting,
    expandedEmployeeId,
    fetchReport,
    toggleExpanded,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    downloadEvidence,
    t2,
  } = useSessionEmployeesPage();

  const viewMode: SessionsViewMode = useMemo(
    () => (location.pathname.endsWith('/empleados') ? 'employees' : 'courses'),
    [location.pathname]
  );

  const viewModeOptions: IconToggleOption<SessionsViewMode>[] = [
    { value: 'employees', tooltip: 'Por empleados', icon: <EmployeesReportIcon /> },
    { value: 'courses', tooltip: 'Por cursos', icon: <CoursesReportIcon /> },
  ];

  const handleViewModeChange = (value: SessionsViewMode) => {
    if (value === viewMode) return;
    history.push(value === 'employees' ? '/reporte/sesiones/empleados' : '/reporte/sesiones/cursos');
  };

  useEffect(() => {
    handleSubmit((values: SessionsReportFilterValues) => fetchReport(values))();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <SideBarContainer>
      <ReportsLayout
        title={<ReportsTitle title={t2('byEmployee.pageTitle')} accent={`"${t2('byEmployee.pageAccent')}"`} />}
        onExport={handleExport}
        buttons={
          <IconToggleGroup
            options={viewModeOptions}
            value={viewMode}
            onChange={handleViewModeChange}
            ariaLabel="Modo de vista"
          />
        }
      />
      <Padding padding="1rem">
        <SessionsReportFilters
          form={form}
          onChange={fetchReport}
          report={null}
          searchPlaceholder={t2('byEmployee.filters.searchPlaceholder')}
          hideGroupBy
        />
        <SessionsReportKpis kpis={report?.kpis ?? null} loading={loading} />
        <SessionsByEmployeeTable
          employees={report?.employees ?? []}
          loading={loading || exporting}
          error={error}
          expandedEmployeeId={expandedEmployeeId}
          pagination={pagination}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onToggleExpanded={toggleExpanded}
          onDownloadEvidence={downloadEvidence}
          t2={t2}
          emptyText={t2('byEmployee.empty.pendingBackend')}
        />
      </Padding>
    </SideBarContainer>
  );
}
